import { AlertTriangle, Loader2 } from "lucide-react"
import { Helmet } from "react-helmet-async"
import { Toaster } from "@/components/ui/sonner"
import { BookingProvider, useBooking } from "@/context/BookingContext"
import Index from "@/pages/Index"
import Checkout from "@/pages/Checkout"
import BookingSuccess from "@/pages/BookingSuccess"
import BookingCancel from "@/pages/BookingCancel"
import Terms from "@/pages/Terms"
import Privacy from "@/pages/Privacy"
import Support from "@/pages/Support"
import { getEventSlugFromUrl } from "@/lib/utils"
import { useBrandingSync } from "@/widgets/core/useBrandingSync"

export type AppMode = "website" | "widget"
export type AppPlacement = "full-app"

export interface AppProps {
  slug?: string
  mode?: AppMode
  placement?: AppPlacement
}

function getRoute(): "booking-success" | "booking-cancel" | "terms" | "privacy" | "support" | null {
  const path = window.location.pathname
  const slug = getEventSlugFromUrl()
  const prefix = slug ? `/event/${slug}` : ""

  if (
    path === `${prefix}/checkout/success` ||
    path === `${prefix}/booking/success` ||
    (!slug && (path === "/checkout/success" || path === "/booking/success"))
  )
    return "booking-success"
  if (
    path === `${prefix}/checkout/cancel` ||
    path === `${prefix}/booking/cancel` ||
    (!slug && (path === "/checkout/cancel" || path === "/booking/cancel"))
  )
    return "booking-cancel"
  if (path === `${prefix}/terms` || (!slug && path === "/terms")) return "terms"
  if (path === `${prefix}/privacy` || (!slug && path === "/privacy"))
    return "privacy"
  if (path === `${prefix}/support` || (!slug && path === "/support"))
    return "support"
  return null
}

// Renders nothing for widgets or non-full-app placements — never touches host page head.
function SeoHead({ mode, placement }: { mode: AppMode; placement: AppPlacement }) {
  const { state } = useBooking()
  const route = getRoute()

  if (mode === "widget" || placement !== "full-app") return null

  const canonical = window.location.origin + window.location.pathname

  if (route === "booking-success") {
    return (
      <Helmet>
        <title>Booking Confirmed | Official Travel</title>
        <meta
          name="description"
          content="Your esports travel booking payment was completed successfully."
        />
        <link rel="canonical" href={canonical} />
      </Helmet>
    )
  }

  if (route === "booking-cancel") {
    return (
      <Helmet>
        <title>Booking Cancelled | Official Travel</title>
        <meta
          name="description"
          content="Your esports travel booking payment was cancelled before completion."
        />
        <link rel="canonical" href={canonical} />
      </Helmet>
    )
  }

  if (route === "support") {
    return (
      <Helmet>
        <title>Support | Official Travel</title>
        <meta
          name="description"
          content="Need help? Contact our support team or send us a message."
        />
        <link rel="canonical" href={canonical} />
      </Helmet>
    )
  }

  const eventName = state.event?.name ?? "Esports Travel Packages"
  const eventLocation = state.event?.location ?? "Event destination"

  const jsonLd =
    state.event?.name && state.event?.startDate && state.event?.location
      ? JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Event",
          name: state.event.name,
          startDate: state.event.startDate,
          endDate: state.event.endDate,
          eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
          eventStatus: "https://schema.org/EventScheduled",
          location: {
            "@type": "Place",
            name: state.event.venue ?? state.event.location,
            address: state.event.location,
          },
        })
      : null

  return (
    <Helmet>
      <title>{eventName} Travel Packages | Official Partner</title>
      <meta
        name="description"
        content={`Book live travel packages for ${eventName} in ${eventLocation}. Compare hotel, flight and add-on options in one checkout.`}
      />
      <link rel="canonical" href={canonical} />
      {jsonLd && (
        <script type="application/ld+json">{jsonLd}</script>
      )}
    </Helmet>
  )
}

// All routing and checkout logic lives here.
// Never rendered for small widget placements.
function FullAppContent({ mode }: { mode: AppMode }) {
  const { state } = useBooking()
  const route = getRoute()

  if (route === "booking-success") return <BookingSuccess />
  if (route === "booking-cancel") return <BookingCancel />

  if (state.eventStatus === "idle" || state.eventStatus === "loading") {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (state.eventStatus === "error") {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <AlertTriangle className="size-10 text-destructive" />
        <h1 className="text-2xl font-bold text-foreground">Event unavailable</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          {state.eventError?.userMessage ?? "This event could not be loaded. Please check the link or try again later."}
        </p>
      </div>
    )
  }

  if (state.currentStep === "checkout") return <Checkout />

  // Only the hosted page owns its URL; the widget runs on a partner's page.
  return <Index syncUrl={mode === "website"} />
}

function AppContent({ mode, placement }: { mode: AppMode; placement: AppPlacement }) {
  useBrandingSync()
  return (
    <>
      <SeoHead mode={mode} placement={placement} />
      <FullAppContent mode={mode} />
    </>
  )
}

export function App({ slug, mode = "website", placement = "full-app" }: AppProps) {
  const route = getRoute()

  if (route === "terms") return <Terms />
  if (route === "privacy") return <Privacy />
  if (route === "support") return <Support />

  return (
    <BookingProvider slug={slug}>
      <AppContent mode={mode} placement={placement} />
      <Toaster position="top-center" />
    </BookingProvider>
  )
}

export default App
